import { useEffect, useRef } from 'react';
import type { Rhythm } from '../../lib/waveforms';

interface Vitals {
  rhythm: Rhythm;
  hr: number;
  spo2: number;
  etco2_mmhg: number;
  rr: number;
}

interface Props {
  lane: 'ecg' | 'capno' | 'pleth';
  vitals: Vitals;
  sim_t_ms: number;
  cpr_active: boolean;
  cpr_quality_pct: number;
  cpr_rate_cpm: number;
  intubated: boolean;
  shock_at_ms?: number;
  height?: number;
  className?: string;
}

export default function WaveformLane({ lane, vitals, sim_t_ms, cpr_active, cpr_quality_pct, cpr_rate_cpm, intubated, shock_at_ms, height = 90, className }: Props) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const workerRef = useRef<Worker | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const w = canvas.clientWidth || 600;
    const h = height;
    canvas.width = Math.floor(w * ratio);
    canvas.height = Math.floor(h * ratio);

    if (typeof OffscreenCanvas !== 'undefined' && typeof Worker !== 'undefined') {
      try {
        const offscreen = canvas.transferControlToOffscreen();
        const worker = new Worker(new URL('./waveform.worker.ts', import.meta.url), { type: 'module' });
        worker.postMessage({ kind: 'init', canvas: offscreen, lane, width: canvas.width, height: canvas.height }, [offscreen]);
        workerRef.current = worker;
      } catch (err) {
        console.warn('OffscreenCanvas worker init failed, falling back to main-thread render', err);
      }
    }
    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
    // run once per lane
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lane]);

  useEffect(() => {
    const w = workerRef.current;
    if (!w) return;
    w.postMessage({
      kind: 'state',
      vitals,
      sim_t_ms,
      cpr_active,
      cpr_quality_pct,
      cpr_rate_cpm,
      intubated,
      shock_at_ms,
    });
  }, [vitals, sim_t_ms, cpr_active, cpr_quality_pct, cpr_rate_cpm, intubated, shock_at_ms]);

  return (
    <canvas
      ref={ref}
      style={{ height, width: '100%', display: 'block' }}
      className={`bg-ink-950 rounded ${className || ''}`}
      aria-label={`${lane} waveform`}
    />
  );
}
